import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../../firebase'
import { getBrandBadgeClass } from '../../utils/brandBadge'
import mainEventerTitleBg from '../../assets/me_title.png'
import {
  getFactionStoredWrestlers,
  getFactionWrestlerIds
} from '../../utils/factionWrestlers'
import {
  getFactionMainEventerStatus,
  getFactionMemberContribution,
  hasFactionScoringStarted
} from '../../utils/factionScoring'
import { calculateHomeFactionTotalScore } from '../../utils/homeScoring'
import {
  formatGmMatchDate,
  formatGmMatchParticipants,
  getGmMatchLifecycle,
  getGmMatchLabel,
  isGmMatchInProgress
} from '../../utils/gmMatches'
import {
  formatSignedPoints,
  getLastShowBannerMessage,
  getLastShowContributorBonusMeta
} from '../../utils/lastShowSummary'
import { isShowInLiveWindow, toShowDate } from '../../utils/showLiveWindow'

const renderLastShowContributorBonus = entry => {
  const bonusMeta = getLastShowContributorBonusMeta(entry?.status)
  if (!bonusMeta) return null

  return (
    <span
      className={`faction-last-show-banner__member-bonus ${bonusMeta.toneClassName}`}
    >
      {bonusMeta.label}
    </span>
  )
}

function QuickInfoCards ({
  userFaction,
  nextShowInfo,
  userStats,
  allWrestlers = [],
  nextShowParticipants = [],
  gmMatches = [],
  lastShowSummary = null
}) {
  const navigate = useNavigate()
  const [countdown, setCountdown] = useState({
    isOnAir: false,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  })
  const [topWrestler, setTopWrestler] = useState(null)
  const [participatingWrestlers, setParticipatingWrestlers] = useState([])
  const [resolvedRoster, setResolvedRoster] = useState(null)
  const [resolvedRosterName, setResolvedRosterName] = useState('')
  const displayedFaction = userFaction || null
  const effectiveFaction = useMemo(
    () =>
      displayedFaction
        ? {
            ...displayedFaction,
            dataInizioPunteggio:
              resolvedRoster?.dataInizioPunteggio ||
              displayedFaction.dataInizioPunteggio
          }
        : null,
    [displayedFaction, resolvedRoster]
  )
  const factionWrestlers = useMemo(
    () => getFactionStoredWrestlers(effectiveFaction),
    [effectiveFaction]
  )
  const scoringStarted = useMemo(
    () => hasFactionScoringStarted(effectiveFaction),
    [effectiveFaction]
  )
  const openGmMatches = useMemo(
    () =>
      gmMatches
        .filter(match => isGmMatchInProgress(match))
        .sort(
          (a, b) =>
            (a.weekStart?.getTime?.() || 0) - (b.weekStart?.getTime?.() || 0)
        ),
    [gmMatches]
  )
  const latestClosedGmMatch = useMemo(
    () =>
      gmMatches.find(match => getGmMatchLifecycle(match) === 'closed') || null,
    [gmMatches]
  )
  const mainEventerStatus = useMemo(
    () =>
      getFactionMainEventerStatus({
        faction: effectiveFaction,
        roster: resolvedRoster
      }),
    [effectiveFaction, resolvedRoster]
  )

  useEffect(() => {
    if (!displayedFaction) {
      setResolvedRoster(null)
      setResolvedRosterName('')
      return
    }

    const fallbackRosterName = displayedFaction.roster || 'Roster non assegnato'
    setResolvedRoster(null)
    setResolvedRosterName(fallbackRosterName)

    if (!displayedFaction.rosterId) {
      return
    }

    let isMounted = true

    const loadRosterName = async () => {
      try {
        const rosterDoc = await getDoc(
          doc(db, 'rosters', displayedFaction.rosterId)
        )
        if (!isMounted) return

        if (rosterDoc.exists()) {
          const rosterData = { id: rosterDoc.id, ...rosterDoc.data() }
          setResolvedRoster(rosterData)
          setResolvedRosterName(rosterData.name || fallbackRosterName)
        }
      } catch (error) {
        console.error('Errore caricamento roster della fazione:', error)
      }
    }

    loadRosterName()

    return () => {
      isMounted = false
    }
  }, [displayedFaction])

  // Calcola il punteggio totale della fazione
  const factionTotalScore = useMemo(() => {
    if (!factionWrestlers.length || !allWrestlers.length) return 0
    const wrestlerScoresById = {}
    allWrestlers.forEach(wrestler => {
      wrestlerScoresById[wrestler.id] = wrestler.score || 0
    })

    return calculateHomeFactionTotalScore({
      faction: effectiveFaction,
      roster: resolvedRoster,
      members: factionWrestlers,
      wrestlerScoresById
    })
  }, [allWrestlers, effectiveFaction, factionWrestlers, resolvedRoster])

  // Calcola il punteggio medio della fazione
  const averageScore = useMemo(() => {
    if (!factionWrestlers.length) return 0
    return Math.round(factionTotalScore / factionWrestlers.length)
  }, [factionTotalScore, factionWrestlers.length])
  const lastShowContributors = useMemo(() => {
    if (!Array.isArray(lastShowSummary?.contributors)) return []
    return lastShowSummary.contributors
  }, [lastShowSummary])
  const lastShowGroups = useMemo(() => {
    if (!Array.isArray(lastShowSummary?.showSummaries)) return []
    return lastShowSummary.showSummaries
  }, [lastShowSummary])
  const hasMultipleLastShows = lastShowGroups.length > 1
  const lastShowBannerMessage = useMemo(() => {
    if (!lastShowContributors.length) {
      return 'Stavolta nessuno dei tuoi wrestler ha lasciato il segno.'
    }

    return getLastShowBannerMessage({
      totalPoints: lastShowSummary?.totalPoints || 0,
      seed: `${displayedFaction?.id || ''}:${
        lastShowSummary?.showId || ''
      }:home`
    })
  }, [displayedFaction?.id, lastShowContributors.length, lastShowSummary])

  // Calcola countdown dettagliato con logica "in onda"
  useEffect(() => {
    if (!nextShowInfo?.data) return

    const updateCountdown = () => {
      const now = new Date()
      const showDate = toShowDate(nextShowInfo.data)
      if (!showDate) return

      const diff = showDate - now

      if (isShowInLiveWindow(nextShowInfo, now)) {
        setCountdown({
          isOnAir: true,
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        })
      } else if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24))
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        )
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((diff % (1000 * 60)) / 1000)

        setCountdown({
          isOnAir: false,
          days,
          hours,
          minutes,
          seconds
        })
      } else {
        setCountdown({
          isOnAir: false,
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        })
      }
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [nextShowInfo])

  // Trova il wrestler con più punti nella fazione
  useEffect(() => {
    if (factionWrestlers.length > 0 && allWrestlers.length > 0) {
      const buildTopWrestler = member => {
        const wrestler = allWrestlers.find(w => w.id === member.id)
        if (!wrestler) return null

        const effectiveStatus = member.status || null
        const points = getFactionMemberContribution({
          faction: effectiveFaction,
          roster: resolvedRoster,
          wrestlerId: member.id,
          currentScore: wrestler.score || 0,
          startScore: member.startScore || 0,
          accumulatedPoints: member.accumulatedPoints || 0,
          status: effectiveStatus
        })

        return {
          id: wrestler.id,
          name: wrestler.name,
          brand: wrestler.brand || 'Indy',
          currentScore: wrestler.score || 0,
          pointsDifference: points,
          imageUrl: wrestler.imageUrl || '/assets/default-wrestler.png',
          status: effectiveStatus
        }
      }

      const assignedMainEventer = factionWrestlers.find(
        member => buildTopWrestler(member)?.status === 'Main_Eventer'
      )

      if (assignedMainEventer) {
        setTopWrestler(buildTopWrestler(assignedMainEventer))
        return
      }

      let top = null
      let maxPoints = -Infinity

      factionWrestlers.forEach(member => {
        const candidate = buildTopWrestler(member)
        if (candidate && candidate.pointsDifference > maxPoints) {
          maxPoints = candidate.pointsDifference
          top = candidate
        }
      })

      setTopWrestler(top)
    } else {
      setTopWrestler(null)
    }
  }, [allWrestlers, effectiveFaction, factionWrestlers, resolvedRoster])

  useEffect(() => {
    const factionIds = getFactionWrestlerIds(displayedFaction)
    if (factionIds.length > 0 && nextShowParticipants?.length > 0) {
      const participating = factionIds
        .filter(wrestlerId => nextShowParticipants.includes(wrestlerId))
        .map(wrestlerId => {
          const wrestler = allWrestlers.find(w => w.id === wrestlerId)
          return wrestler
            ? {
                id: wrestler.id,
                name: wrestler.name || `Wrestler ${wrestlerId.substring(0, 6)}`,
                imageUrl: wrestler.imageUrl || '/assets/default-wrestler.png'
              }
            : null
        })
        .filter(Boolean)

      setParticipatingWrestlers(participating)
    } else {
      setParticipatingWrestlers([])
    }
  }, [displayedFaction, nextShowParticipants, allWrestlers])

  return (
    <div className='row mb-1 mt-1 align-items-stretch'>
      {/* Mia Fazione (col-8 su schermi grandi) */}
      <div className='col-12 col-lg-8 mb-4'>
        <div
          className='card brand-card h-100 border-0 overflow-hidden position-relative'
          style={{
            background:
              'linear-gradient(135deg, var(--brand-primary) 0%, #1d2c39 100%)'
          }}
        >
          {displayedFaction?.imageUrl && (
            <>
              <div
                className='position-absolute top-0 start-0 w-100 h-100'
                style={{
                  background:
                    'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.16), transparent 32%)',
                  zIndex: 0
                }}
              />
              <img
                src={displayedFaction.imageUrl}
                alt=''
                aria-hidden='true'
                className='position-absolute top-0 start-0 w-100 h-100'
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center',
                  transform: 'scale(1.28)',
                  filter: 'blur(2px) saturate(1.05)',
                  opacity: 0.34,
                  zIndex: 0
                }}
                onError={e => {
                  e.currentTarget.style.display = 'none'
                }}
              />
              <div
                className='position-absolute top-0 start-0 w-100 h-100'
                style={{
                  background:
                    'linear-gradient(135deg, rgba(38, 56, 72, 0.30), rgba(26, 38, 50, 0.10))',
                  zIndex: 1
                }}
              />
            </>
          )}
          <div
            className='card-body p-2 position-relative'
            style={{ zIndex: 2 }}
          >
            <div className='row align-items-center'>
              {displayedFaction ? (
                <>
                  {/* Nome Fazione e Immagine */}
                  <div className='col-md-12 mb-3'>
                    <div className='mb-3'>
                      {/* Logo/Immagine Fazione */}
                      <div className='d-flex align-items-center mb-3'>
                        {displayedFaction.imageUrl && (
                          <div className='me-3'>
                            <img
                              src={displayedFaction.imageUrl}
                              alt={displayedFaction.name}
                              className='rounded-circle'
                              style={{
                                width: '70px',
                                height: '70px',
                                objectFit: 'cover'
                              }}
                              onError={e => {
                                e.target.style.display = 'none'
                                const fallback = e.target.nextElementSibling
                                if (fallback) fallback.style.display = 'block'
                              }}
                            />
                          </div>
                        )}
                        <div className='flex-grow-1'>
                          <div className='d-flex align-items-center gap-2 mb-1'>
                            <h2 className='h4 mb-0 text-white'>
                              {displayedFaction.name}
                            </h2>
                          </div>
                          <div
                            className='d-flex align-items-center gap-2'
                            onClick={() =>
                              displayedFaction?.rosterId &&
                              navigate(`/rosters/${displayedFaction.rosterId}`)
                            }
                            style={{
                              cursor: displayedFaction?.rosterId
                                ? 'pointer'
                                : 'default'
                            }}
                          >
                            <span className='badge brand-stat px-3 py-1'>
                              <i className='bi bi-diagram-2-fill me-1'></i>
                              {resolvedRosterName}
                            </span>
                          </div>
                        </div>
                      </div>

                      {mainEventerStatus?.kind === 'expired' && (
                        <div
                          className='rounded-3 px-3 py-2 mb-3 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2'
                          style={{
                            background: 'rgba(255, 193, 7, 0.16)',
                            border: '1px solid rgba(255, 193, 7, 0.72)',
                            color: '#fff7d1'
                          }}
                        >
                          <div>
                            <strong className='profile-alert-badge'>
                              Main eventer scaduto
                            </strong>
                            <span className='small'>
                              Aggiorna la line-up della fazione per continuare a
                              ottenere punti.
                            </span>
                          </div>
                          {displayedFaction?.id && (
                            <button
                              type='button'
                              className='btn btn-sm btn-outline-warning'
                              onClick={() =>
                                navigate(
                                  `/factions/${displayedFaction.id}/lineup`
                                )
                              }
                            >
                              Vai alla line-up
                            </button>
                          )}
                        </div>
                      )}

                      {/* Main Eventer Section */}
                      {topWrestler && (
                        <div
                          className='main-eventer rounded-3 mb-3 overflow-hidden' // ← overflow hidden per tenere tutto dentro il bordo
                          style={{
                            backgroundImage: `linear-gradient(90deg, rgba(8, 14, 24, 0.88), rgba(8, 14, 24, 0.52)), url(${mainEventerTitleBg})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            minHeight: 'clamp(110px, 15vw, 140px)',
                            position: 'relative'
                          }}
                        >
                          <div className='row g-0 h-100'>
                            {/* Colonna immagine: usciamo verso destra con margine negativo */}
                            <div
                              className='col-3'
                              style={{
                                position: 'relative',
                                marginRight: '-2rem'
                              }}
                            >
                              <div
                                className='h-100'
                                style={{ overflow: 'visible' }}
                              >
                                <img
                                  src={topWrestler.imageUrl}
                                  alt={topWrestler.name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover', // copre tutta l'altezza senza deformare
                                    objectPosition: 'center 20%', // regola il focale se serve
                                    display: 'block'
                                  }}
                                  onError={e => {
                                    e.target.src = '/assets/logo.png'
                                  }}
                                />
                              </div>
                            </div>

                            {/* Colonna testo: viene "sotto" l'immagine grazie allo z-index */}
                            <div
                              className='col-9 p-3 d-flex flex-column justify-content-center'
                              style={{
                                position: 'relative',
                                zIndex: 2,
                                backgroundColor: 'transparent' // per vedere l'immagine sotto
                              }}
                            >
                              <div
                                className='d-flex justify-content-between align-items-start h-100'
                                onClick={() =>
                                  topWrestler.id &&
                                  navigate(`/wrestlers/${topWrestler.id}`)
                                }
                                style={{ cursor: 'pointer' }}
                              >
                                <div>
                                  <div
                                    className='text-light small mb-1'
                                    style={{ letterSpacing: '0.5px' }}
                                  >
                                    {topWrestler.status === 'Main_Eventer'
                                      ? 'IL TUO MAIN EVENTER'
                                      : 'IL TUO TOP WRESTLER'}
                                  </div>
                                  <h5 className='text-white fw-bold mb-2'>
                                    {topWrestler.name}
                                  </h5>
                                  <div className='d-flex align-items-center gap-2'>
                                    <span
                                      className={`${getBrandBadgeClass(
                                        topWrestler.brand
                                      )} badge px-2 py-1`}
                                    >
                                      {topWrestler.brand}
                                    </span>
                                  </div>
                                </div>

                                {/* Punteggio con sfondo brand-primary e testo brand-secondary */}
                                <div className='text-end'>
                                  <div className='flex-fill brand-stat rounded-3 p-3 text-center text-brand-secondary'>
                                    <div
                                      className='fw-bold'
                                      style={{
                                        fontSize: '1.5rem',
                                        background: (26, 38, 50, 0.1)
                                      }}
                                    >
                                      {scoringStarted
                                        ? topWrestler.pointsDifference
                                        : 0}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {scoringStarted && (
                        <div className='faction-last-show-banner mt-3'>
                          <div className='faction-last-show-banner__top'>
                            <div className='faction-last-show-banner__copy'>
                              {hasMultipleLastShows ? (
                                <div className='faction-last-show-banner__shows'>
                                  {lastShowGroups.map(showGroup => (
                                    <div
                                      key={`${showGroup.showId || showGroup.showName}-${showGroup.totalPoints}`}
                                      className='faction-last-show-banner__show'
                                    >
                                      <div className='faction-last-show-banner__show-head'>
                                        <span className='faction-last-show-banner__show-name'>
                                          {showGroup.showName || 'Show'}
                                        </span>
                                        <strong
                                          className={`faction-last-show-banner__show-total ${showGroup.totalPoints < 0 ? 'is-negative' : ''}`}
                                        >
                                          {formatSignedPoints(showGroup.totalPoints)}
                                        </strong>
                                      </div>
                                      <div className='faction-last-show-banner__members'>
                                        {showGroup.contributors.map(entry => (
                                          <div
                                            key={`${showGroup.showId}-${entry.id}-${entry.points}`}
                                            className='faction-last-show-banner__member'
                                          >
                                            <div className='faction-last-show-banner__member-copy'>
                                              <span className='faction-last-show-banner__member-name'>
                                                {entry.name}
                                              </span>
                                            </div>
                                            <strong
                                              className={`faction-last-show-banner__member-points ${entry.points < 0 ? 'is-negative' : ''}`}
                                            >
                                              <span>{formatSignedPoints(entry.points)}</span>
                                              {renderLastShowContributorBonus(entry)}
                                            </strong>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : lastShowContributors.length > 0 ? (
                                <div className='faction-last-show-banner__members'>
                                  {lastShowContributors.map(entry => (
                                    <div
                                      key={`${entry.id}-${entry.points}`}
                                      className='faction-last-show-banner__member'
                                    >
                                      <div className='faction-last-show-banner__member-copy'>
                                        <span className='faction-last-show-banner__member-name'>
                                          {entry.name}
                                        </span>
                                      </div>
                                      <strong
                                        className={`faction-last-show-banner__member-points ${entry.points < 0 ? 'is-negative' : ''}`}
                                      >
                                        <span>{formatSignedPoints(entry.points)}</span>
                                        {renderLastShowContributorBonus(entry)}
                                      </strong>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className='mb-0 faction-last-show-banner__empty'>
                                  Nessun tuo wrestler ha fatto punti nell ultimo show
                                </p>
                              )}
                            </div>
                            <div
                              className={`faction-last-show-banner__score ${lastShowSummary?.totalPoints < 0 ? 'is-negative' : ''}`}
                            >
                              <span className='faction-last-show-banner__score-label'>
                                Totale utlimo show
                              </span>
                              <strong>
                                {formatSignedPoints(lastShowSummary?.totalPoints || 0)}
                              </strong>
                            </div>
                          </div>
                          <div className='faction-last-show-banner__message'>
                            {lastShowBannerMessage}
                          </div>
                        </div>
                      )}
<div className='home-micro-stats mt-3'>
  <div className='home-micro-stat'>
    <i className='bi bi-currency-dollar me-2 fs-6 text-success'></i>
    <span className='stat-label'>Crediti rimanenti</span>
    <span className='stat-value text-success'>{displayedFaction.creditsLeft || 0}</span>
  </div>
  <div className='home-micro-stat'>
    <i className='bi bi-bar-chart-fill me-2 fs-6 brand-title'></i>
    <span className='stat-label'>Media punteggio wrestler</span>
    <span className='stat-value brand-title'>{averageScore}</span>
  </div>
</div>

                      {(openGmMatches.length > 0 || latestClosedGmMatch) && (
                        <div className='brand-stat rounded-3 p-3 mt-3'>
                          <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
                            <h6 className='text-white mb-0'>
                              <i className='bi bi-controller me-2 brand-title'></i>
                              I tuoi match
                            </h6>
                            {openGmMatches.length > 0 && (
                              <span className='badge bg-success'>
                                {openGmMatches.length} in corso
                              </span>
                            )}
                          </div>

                          {openGmMatches.length > 0 ? (
                            <div className='d-grid gap-2'>
                              {openGmMatches.slice(0, 2).map(match => (
                                <div
                                  key={match.id}
                                  className='countdown-box rounded-3 p-2'
                                >
                                  <div className='d-flex justify-content-between gap-2'>
                                    <span className='badge bg-dark align-self-start'>
                                      {getGmMatchLabel(match.matchType)}
                                    </span>
                                    <span className='small text-light opacity-75'>
                                      {formatGmMatchDate(match.weekStart)} -{' '}
                                      {formatGmMatchDate(match.weekEnd)}
                                    </span>
                                  </div>
                                  <div className='text-white small mt-2'>
                                    {formatGmMatchParticipants(
                                      match,
                                      displayedFaction.id
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className='small text-light opacity-75'>
                              Ultimo match:{' '}
                              {formatGmMatchParticipants(
                                latestClosedGmMatch,
                                displayedFaction.id
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bottone */}
                  <div className='col-md-12'>
                    <div className='mt-1'>
                      <button
                        className='btn btn-show btn-lg w-100 d-flex justify-content-between align-items-center py-3'
                        onClick={e => {
                          e.stopPropagation()
                          navigate(`/profile`)
                        }}
                      >
                        <div className='d-flex align-items-center text-truncate'>
                          <span className='fw-bold'>Vedi dettagli fazione</span>
                        </div>
                        <i className='bi bi-arrow-right-circle fs-4'></i>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className='col-12 text-center py-3'>
                  <div className='p-3'>
                    <i className='bi bi-emoji-frown fs-1 mb-3 d-block brand-title'></i>
                    <h4 className='text-white mb-2'>
                      Non hai ancora una fazione
                    </h4>
                    <p className='text-light opacity-75 mb-4'>
                      Crea la tua fazione e inizia a competere con gli altri
                      giocatori!
                    </p>
                    <button
                      className='btn btn-show btn-lg px-5'
                      onClick={e => {
                        e.stopPropagation()
                        navigate('/profile')
                      }}
                    >
                      <i className='bi bi-plus-circle me-2'></i>
                      Crea fazione ora
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Prossimo Show (col-4 su schermi grandi) */}
      <div className='col-12 col-lg-4 mb-4'>
        <div
          className='card brand-card h-100 border-0 overflow-hidden position-relative'
          style={{
            backgroundImage: nextShowInfo?.sfondo
              ? `linear-gradient(rgba(38, 56, 72, 0.6), rgba(0, 0, 0, 0.85)), url(${nextShowInfo.sfondo})`
              : 'linear-gradient(135deg, var(--brand-primary) 0%, #1d2c39 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            cursor: 'pointer'
          }}
          onClick={() =>
            nextShowInfo?.id && navigate(`/shows/${nextShowInfo.id}`)
          }
        >
          <div className='card-body p-4 d-flex flex-column position-relative'>
            {/* Logo Show */}
            {nextShowInfo?.logo && (
              <div className='text-center mb-4'>
                <img
                  src={nextShowInfo.logo}
                  alt={nextShowInfo.nome}
                  className='img-fluid show-logo'
                  style={{
                    maxHeight: '60px',
                    maxWidth: '100%'
                  }}
                />
              </div>
            )}

            {nextShowInfo ? (
              <>
                {/* Data */}
                <div className='text-center text-white mb-4'>
                  <i className='bi bi-calendar-event me-2 brand-title'></i>
                  <span className='brand-subtitle'>
                    {nextShowInfo.formattedDate}
                  </span>
                </div>

                {/* Countdown */}
                <div className='mb-4'>
                  {countdown.isOnAir ? (
                    <div className='text-center'>
                      <div className='countdown-box rounded-3 p-3 w-100'>
                        <div className='d-flex align-items-center justify-content-center'>
                          <div className='position-relative me-3'>
                            <span className='results-live-overlay__dot'></span>
                          </div>
                          <div style={{ width: '100%' }}>
                            <div className='h4 mb-0 text-white fw-bold'>
                              SHOW IN LIVE
                            </div>
                            <div
                              className='text-light small'
                              style={{ fontSize: '0.8rem' }}
                            >
                              Lo show è in corso!
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className='row g-1 text-center'>
                      {[
                        {
                          value: countdown.days,
                          label: 'Giorni',
                          color: 'var(--brand-secondary)'
                        },
                        {
                          value: countdown.hours,
                          label: 'Ore',
                          color: 'var(--brand-secondary)'
                        },
                        {
                          value: countdown.minutes,
                          label: 'Min',
                          color: 'var(--brand-secondary)'
                        },
                        {
                          value: countdown.seconds,
                          label: 'Sec',
                          color: 'var(--brand-secondary)'
                        }
                      ].map((item, index) => (
                        <div className='col-3' key={index}>
                          <div className='countdown-box rounded-3 p-2'>
                            <div
                              className='countdown-number'
                              style={{ color: item.color }}
                            >
                              {item.value.toString().padStart(2, '0')}
                            </div>
                            <div className='countdown-label'>{item.label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Wrestler che partecipano */}
                {participatingWrestlers.length > 0 ? (
                  <div className='mb-4 flex-grow-1'>
                    <div className='countdown-box rounded-3 p-3'>
                      <h6 className='brand-title mb-3 d-flex align-items-center'>
                        <span className='badge brand-badge me-2'>
                          {participatingWrestlers.length}
                        </span>
                        dei tuoi wrestler partecipa
                        {participatingWrestlers.length > 1 ? 'no' : ''}!
                      </h6>

                      <div className='row'>
                        {participatingWrestlers.map(wrestler => (
                          <div
                            key={wrestler.id}
                            className='col-lg-6 col-12 mb-1'
                          >
                            <div className='participant-item rounded-3 p-1'>
                              <div className='d-flex align-items-center'>
                                <div className='flex-shrink-0 me-2 position-relative'>
                                  <img
                                    src={wrestler.imageUrl}
                                    alt={wrestler.name}
                                    className='rounded-circle'
                                    style={{
                                      width: '35px',
                                      height: '35px',
                                      objectFit: 'cover'
                                    }}
                                  />
                                </div>
                                <div className='flex-grow-1'>
                                  <div
                                    className='text-white text-truncate'
                                    style={{
                                      fontSize: '0.9rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {wrestler.name}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='mb-4 flex-grow-1 d-flex align-items-center justify-content-center'>
                    <div className='text-center py-4'>
                      <p
                        className='text-light opacity-75 mb-0'
                        style={{ fontSize: '0.9rem' }}
                      >
                        Nessuno dei tuoi wrestler è stato annunciato
                      </p>
                    </div>
                  </div>
                )}

                {/* Bottone */}
                <div className='mt-auto'>
                  <button
                    className='btn btn-show w-100 d-flex justify-content-between align-items-center py-3'
                    onClick={e => {
                      e.stopPropagation()
                      navigate(`/shows/${nextShowInfo.id}`)
                    }}
                  >
                    <div className='d-flex align-items-center text-truncate'>
                      <span className='fw-bold'>
                        Vedi tutti i wrestler annunciati
                      </span>
                    </div>
                    <i className='bi bi-arrow-right-circle fs-4'></i>
                  </button>
                </div>
              </>
            ) : (
              <div className='text-center py-5 my-auto'>
                <div className='empty-state rounded-3 p-4'>
                  <i className='bi bi-calendar-x fs-1 mb-3 d-block brand-title'></i>
                  <h5 className='text-white mb-2'>Nessuno show in programma</h5>
                  <p className='text-light opacity-75 mb-0'>
                    Controlla più tardi per i prossimi eventi
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuickInfoCards
